import { DAYS } from "../constants";

const DAY_NAMES_SHORT = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

/**
 * Convert time string "HH:mm" to minutes since midnight.
 */
export function timeToMinutes(t) {
  if (!t || typeof t !== "string") return 0;
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/**
 * Convert a Date to local date string (YYYY-MM-DD).
 * NE JAMAIS utiliser toISOString().slice(0,10) — UTC vs local cause des décalages.
 */
export function toLocalDateString(date = new Date()) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/**
 * Get ISO date (YYYY-MM-DD) for a day index (0=Mon, 1=Tue, ...) in the current week.
 */
export function getWeekDate(dayIndex, refDate = new Date()) {
  const d = new Date(refDate);
  const dow = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((dow + 6) % 7));
  const target = new Date(monday);
  target.setDate(monday.getDate() + dayIndex);
  const y = target.getFullYear();
  const m = String(target.getMonth() + 1).padStart(2, "0");
  const day = String(target.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Get array of ISO dates for the current week (Mon–Sun).
 * Toujours 7 dates (index 0–6). TimeGrid slice avec dayCount pour 5 ou 7 colonnes.
 * Optional offset: 0 = this week, -1 = last week, 1 = next week.
 */
export function getWeekDates(offset = 0, refDate = new Date()) {
  const d = new Date(refDate);
  d.setDate(d.getDate() + offset * 7);
  return Array.from({ length: 7 }, (_, i) => getWeekDate(i, d));
}

/**
 * Normalize day label for comparison (e.g. "Lun 09" → "Lun 9").
 */
export function normalizeDayLabel(dayLabel) {
  if (!dayLabel || typeof dayLabel !== "string") return null;
  const match = dayLabel.trim().match(/^(Lun|Mar|Mer|Jeu|Ven|Sam|Dim)\s+(\d+)$/i);
  if (!match) return null;
  const dayIdx = DAYS.findIndex((d) => d.toLowerCase() === match[1].toLowerCase());
  if (dayIdx < 0) return null;
  return `${DAYS[dayIdx]} ${parseInt(match[2], 10)}`;
}

/**
 * Convert ISO date "2026-03-17" to day label "Lun 17" (for display / matching).
 */
export function isoToDayLabel(dateISO) {
  if (!dateISO) return null;
  const d = new Date(dateISO + "T12:00:00");
  if (isNaN(d.getTime())) return null;
  const idx = (d.getDay() + 6) % 7;
  return `${DAYS[idx]} ${d.getDate()}`;
}

/**
 * Parse day label "Lun 9" to ISO date.
 * Format: "Jour Numéro" = jour de la semaine + numéro du jour (ex: "Lun 9" = lundi 9).
 * Cherche dans la semaine courante puis ±2 semaines pour la migration.
 */
export function parseDayLabelToISO(dayLabel, refDate = new Date()) {
  if (!dayLabel || typeof dayLabel !== "string") return null;
  const trimmed = dayLabel.trim();
  const match = trimmed.match(/^(Lun|Mar|Mer|Jeu|Ven|Sam|Dim)\s+(\d+)$/i);
  if (!match) return null;
  const dayIdx = DAYS.findIndex((d) => d.toLowerCase() === match[1].toLowerCase());
  if (dayIdx < 0) return null;
  const normalizedLabel = `${DAYS[dayIdx]} ${parseInt(match[2], 10)}`;
  for (let offset = -2; offset <= 2; offset++) {
    const weekDates = getWeekDates(offset, refDate);
    for (const dateISO of weekDates) {
      if (isoToDayLabel(dateISO) === normalizedLabel) return dateISO;
    }
  }
  return null;
}

/**
 * Fallback: convert "Lun 9" to ISO using current month/year.
 * Used when parseDayLabelToISO fails (hors plage ±2 semaines).
 */
export function dayLabelToISOFallback(dayLabel, refDate = new Date()) {
  if (!dayLabel || typeof dayLabel !== "string") return null;
  const match = dayLabel.trim().match(/^(Lun|Mar|Mer|Jeu|Ven|Sam|Dim)\s+(\d+)$/i);
  if (!match) return null;
  const dayNum = parseInt(match[2], 10);
  if (dayNum < 1 || dayNum > 31) return null;
  const d = new Date(refDate.getFullYear(), refDate.getMonth(), dayNum);
  if (d.getDate() !== dayNum) return null;
  return toLocalDateString(d);
}

/**
 * Normalize a date string to ISO format (YYYY-MM-DD).
 * Handles "2026-3-10" -> "2026-03-10" and invalid values.
 */
export function normalizeToISO(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const trimmed = dateStr.trim();
  const parsed = new Date(trimmed.includes("T") ? trimmed : trimmed + "T12:00:00");
  if (isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const dd = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/**
 * Check if an ISO date is today.
 */
function toISODate(d) {
  return toLocalDateString(d);
}

export function isToday(dateISO, refDate = new Date()) {
  if (!dateISO) return false;
  return dateISO === toISODate(refDate);
}

/**
 * Check if an ISO date is in the past.
 */
export function isPast(dateISO, refDate = new Date()) {
  if (!dateISO) return false;
  return dateISO < toISODate(refDate);
}

/**
 * Format week range for header display (e.g. "17–21 mars 2026").
 * dayCount: 5 for Lun–Ven, 7 for Lun–Dim.
 */
export function formatWeekRangeLabel(weekDates, dayCount = 5) {
  if (!weekDates || weekDates.length === 0) return "";
  const lastIdx = Math.min(dayCount - 1, weekDates.length - 1);
  const start = new Date(weekDates[0] + "T12:00:00");
  const end = new Date(weekDates[lastIdx] + "T12:00:00");
  const monthNames = ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];
  return `${start.getDate()}–${end.getDate()} ${monthNames[end.getMonth()]} ${end.getFullYear()}`;
}

/**
 * Get week number (ISO) for a date.
 */
export function getWeekNumber(refDate = new Date()) {
  const d = new Date(refDate);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Generate the current week's day labels (e.g. ["Lun 3", "Mar 4", ...]).
 * Week starts on Monday.
 */
export function getWeekDays(refDate = new Date()) {
  const d = new Date(refDate);
  const dayOfWeek = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((dayOfWeek + 6) % 7));

  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return `${DAYS[i]} ${day.getDate()}`;
  });
}

/**
 * Get today's label in "Lun 3" format for matching against events.
 */
export function getTodayLabel(refDate = new Date()) {
  const d = new Date(refDate);
  return `${DAY_NAMES_SHORT[d.getDay()]} ${d.getDate()}`;
}

/**
 * Check if a session date string falls within [weekStart, weekEnd[.
 */
export function isSessionInWeek(dateStr, weekStart, weekEnd) {
  if (!dateStr) return false;
  const parsed = parseFrenchDate(dateStr);
  if (!parsed) return false;
  parsed.setHours(0, 0, 0, 0);
  const start = new Date(weekStart);
  start.setHours(0, 0, 0, 0);
  const end = new Date(weekEnd);
  end.setHours(0, 0, 0, 0);
  return parsed >= start && parsed < end;
}

/**
 * Check if a date string (e.g. "15 janv. 2026") falls in a given month/year.
 */
export function isDateInMonth(dateStr, month, year) {
  if (!dateStr) return false;
  const parsed = parseFrenchDate(dateStr);
  if (!parsed) return false;
  return parsed.getMonth() + 1 === month && parsed.getFullYear() === year;
}

/**
 * Convert ISO date to French date string (e.g. "17 mars 2026").
 */
export function isoToFrenchDate(dateISO) {
  if (!dateISO) return null;
  const d = new Date(dateISO + "T12:00:00");
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Convert a day label like "Lun 10" to a French date string using current week context.
 */
export function dayLabelToFrenchDate(dayLabel) {
  const now = new Date();
  const dow = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((dow + 6) % 7));

  const week = getWeekDays();
  const idx = week.indexOf(dayLabel);
  if (idx < 0) return null;

  const target = new Date(monday);
  target.setDate(monday.getDate() + idx);
  return target.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

const FR_MONTHS = {
  "janv": 0, "jan": 0, "févr": 1, "fév": 1, "feb": 1,
  "mars": 2, "mar": 2, "avr": 3, "apr": 3,
  "mai": 4, "may": 4, "juin": 5, "jun": 5,
  "juil": 6, "jul": 6, "août": 7, "aou": 7, "aug": 7,
  "sept": 8, "sep": 8, "oct": 9, "nov": 10, "déc": 11, "dec": 11,
};

/**
 * Parse a French date string like "15 janv. 2026" or "6 mars 2026".
 * Accepte aussi le format ISO YYYY-MM-DD pour robustesse cross-format.
 */
export function parseFrenchDate(dateStr) {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return new Date(dateStr + "T00:00:00");
  const clean = dateStr.toLowerCase().replace(/\./g, "").trim();
  const parts = clean.split(/\s+/);
  if (parts.length < 2) return null;

  const day = parseInt(parts[0]);
  if (isNaN(day)) return null;

  const monthStr = parts[1];
  const monthIdx = FR_MONTHS[monthStr];
  if (monthIdx === undefined) return null;

  const year = parts[2] ? parseInt(parts[2]) : new Date().getFullYear();
  if (isNaN(year)) return null;

  return new Date(year, monthIdx, day);
}

/**
 * Indique si un événement calendrier (date ISO + time "HH:mm") est strictement dans le futur.
 * Utilisé pour la réactivation standby (ne pas ressusciter des créneaux déjà passés).
 */
export function isCalendarEventInFuture(event) {
  if (!event?.date) return false;
  const timeStr = event.time || "23:59";
  const normalized = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const d = new Date(`${event.date}T${normalized}`);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() > Date.now();
}
